import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useId,
  useDeferredValue,
} from "react";
import { cn } from "@/lib/utils";
import LegislationDocumentHeader from "./LegislationDocumentHeader";
import { Legislation } from "@/models/legislation/Legislation";
import { TimelineItem } from "@/models/legislation/Timeline";
import {
  getDisplayName,
  PARAGRAPH_NAVIGATION_SUPPORTED_SOURCES,
} from "@/config/datasources";
import { handlePrintDocumentOnly } from "@/partials/document-renderer/utils/helpers";
import useLegislationPdfUrl from "@/hooks/useLegislationPdfUrl";
import CompactTimeline from "@/partials/document-renderer/components/document-timeline/CompactTimeline";
import TimelineScopeSwitcher from "@/partials/document-renderer/components/document-timeline/TimelineScopeSwitcher";
import type { TimelineScope } from "@/lib/timelineUniverseParams";
import type { TimelineSelection } from "@/partials/document-renderer/components/document-timeline/timelineNodeAppearance";
import {
  findFlatTimelineIndicesForAccession,
  resolveCurrentTimelineDocumentDisplay,
  resolveDefaultFlatTimelineIndex,
  resolveWindowAnchorForFlatIndex,
} from "@/partials/document-renderer/components/document-timeline/timelineNodeAppearance";
import { flattenTimelineItems } from "@/lib/consolidationViewerUtils";
import { normalizeTimelineItems } from "@/lib/bekTimelineNormalization";
import DocumentViewerToolbar from "@/partials/document-renderer/components/DocumentViewerToolbar";
import DocumentRenderer from "@/partials/document-renderer/DocumentRenderer";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import useUsers from "@/hooks/useUsers";
import ScrollToTopFab from "@/partials/document-renderer/components/ScrollToTopFab";
import ProcessingBanner from "./ProcessingBanner";
import ConsolidationNotFoundBanner from "./ConsolidationNotFoundBanner";
import ConsolidationChangeInstructionPanel from "@/components/dialogs/ConsolidationChangeInstructionPanel";
import OutboundConsolidationsPanel from "./OutboundConsolidationsPanel";
import ConsolidationVerifiedBanner from "./ConsolidationVerifiedBanner";
import LegislationPrintHeader from "./LegislationPrintHeader";
import ConsolidationCoverageBadge from "@/partials/document-renderer/components/document-timeline/ConsolidationCoverageBadge";
import { useIsScrolledPast } from "@/hooks/useIsScrolledPast";
import DiffNavigationFab from "./DiffNavigationFab";
import LegislationContentErrorBoundary from "./LegislationContentErrorBoundary";
import {
  getRetsinformationMetadataFromLegislation,
  resolveAnchorLawAccessionForTimeline,
  resolveTimelineNavigationTarget,
} from "@/lib/consolidationLegislationUtils";
import {
  buildConsolidationViewerPath,
  ConsolidationView,
} from "@/lib/consolidationMetadata";
import {
  ConsolidationChangeStatus,
  InboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import { getConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import {
  countConsolidationAmendmentsAfter,
  resolveTimelineFamilyRoot,
} from "@/lib/timelineUniverseParams";
import {
  getCompareWindowAccessions,
  isConsolidationTimelineComparisonAvailable,
} from "@/lib/consolidationComparisonUtils";
import { ConsolidationRenderContext } from "@/hooks/useLegislationViewerContent";
import { DeletedContainerChange } from "@/models/consolidation";
import {
  EMPTY_CONTAINER_GROUP_KEYS,
  EMPTY_CONTAINER_IDS,
  EMPTY_CONTAINER_STATUS_BY_ID,
  EMPTY_DELETED_CONTAINER_CHANGES,
} from "@/partials/document-renderer/utils/stableEmptyProps";
import { useConsolidationTimelineMetadata } from "@/hooks/useConsolidationTimelineMetadata";
import { useConsolidationActivationDates } from "@/hooks/useConsolidation";
import type { ConsolidationVersionProps } from "./LegislationDocumentHeader";
import { shouldShowConsolidationHistoricWatermark } from "@/lib/consolidationViewerUtils";
import { useParagraphExtraction } from "@/partials/document-renderer/hooks/useParagraphExtraction";
import { resolveDocumentJumpNavigation } from "@/lib/tocUtils";
import { useDocumentSearch } from "@/partials/document-renderer/hooks/useDocumentSearch";
import {
  findScrollContainer,
  calculateScrollOffset,
} from "@/partials/document-renderer/utils/scrollUtils";
import useLegislationComparison from "@/hooks/useLegislationComparison";
import { useDiffNavigation } from "@/hooks/useDiffNavigation";
import {
  useConsolidationChangeNavigation,
  scrollChangedContainerIntoView,
} from "@/hooks/useConsolidationChangeNavigation";
import { buildReconstructedDiffViewHtml } from "@/lib/diffViewHtml";
import {
  X,
  Loader2,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  FileSearch,
  Highlighter,
} from "lucide-react";
import { Label, Switch, useSidebar } from "@capacit-com/lara-law-shared-ui";
import useSidebarStore from "@/stores/sidebar.store";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { useContainerAmendmentDiff } from "@/hooks/useContainerAmendmentDiff";

// ─── ComparisonBar ────────────────────────────────────────────────────────────
// Extracted into its own component so the main component body stays clean.

interface ComparisonBarProps {
  isComputing: boolean;
  fetchedCount: number;
  totalToFetch: number;
  steps: { diffHtml: string | null; error: string | null }[];
  totalSteps: number;
  compareStepIndex: number;
  activeStepBeforeInfo: { shortName: string; year?: number } | null | undefined;
  activeStepAfterInfo: { shortName: string; year?: number } | null | undefined;
  compareTargetInfo: {
    shortName: string;
    year?: number;
    accessionNumber?: string;
  };
  diffError: string | null;
  diffHtml: string | null;
  diffFilter: "diff" | "before" | "after";
  setDiffFilter: (f: "diff" | "before" | "after") => void;
  allChangesCount: number;
  totalChanges: number;
  diffCurrentIndex: number;
  diffGoToPrevious: () => void;
  diffGoToNext: () => void;
  deletions: number;
  insertions: number;
  currentVersionShortName: string | null;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  exitCompareMode: () => void;
}

const ComparisonBar: React.FC<ComparisonBarProps> = ({
  isComputing,
  fetchedCount,
  totalToFetch,
  steps,
  totalSteps,
  compareStepIndex,
  activeStepBeforeInfo,
  activeStepAfterInfo,
  compareTargetInfo,
  diffError,
  diffHtml,
  diffFilter,
  setDiffFilter,
  allChangesCount,
  totalChanges,
  diffCurrentIndex,
  diffGoToPrevious,
  diffGoToNext,
  deletions,
  insertions,
  currentVersionShortName,
  goToPreviousStep,
  goToNextStep,
  exitCompareMode,
}) => {
  const [hintDismissed, setHintDismissed] = useState(false);
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const hintId = useId();

  const beforeName =
    activeStepBeforeInfo?.shortName ?? compareTargetInfo.shortName;
  const afterName =
    activeStepAfterInfo?.shortName ?? currentVersionShortName ?? "";

  // Button base classes shared across all three view-mode buttons
  const viewBtnBase =
    "px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning";

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-2">
      <div className="flex flex-col gap-0 rounded-md border border-warning/50 bg-warning/10 dark:bg-warning/20 dark:border-warning/80 text-sm overflow-hidden">
        {/* ── Row 1: context — what we're comparing, step nav, close ── */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
          {isComputing ? (
            /* Loading state */
            <>
              <Loader2 className="size-4 shrink-0 animate-spin text-warning" />
              <span className="text-warning-foreground text-xs">
                {totalToFetch > 0 && fetchedCount < totalToFetch
                  ? `Henter versioner… ${fetchedCount}/${totalToFetch}`
                  : "Beregner ændringer…"}
              </span>
              {steps.filter((s) => s.diffHtml !== null).length > 0 && (
                <span className="text-warning text-xs opacity-70">
                  ({steps.filter((s) => s.diffHtml !== null).length}/
                  {totalSteps} trin klar)
                </span>
              )}
            </>
          ) : diffError ? (
            /* Error state */
            <>
              <AlertCircle className="size-4 shrink-0 text-destructive" />
              <span className="text-destructive text-xs">{diffError}</span>
            </>
          ) : (
            /* Ready state */
            <>
              {/* "Comparing from X" anchor */}
              <span className="text-warning-foreground shrink-0 min-w-0 flex items-center gap-1.5 flex-wrap">
                <span className="shrink-0 text-warning text-xs font-medium uppercase tracking-wide">
                  Sammenligning
                </span>
                <span className="text-muted-foreground">·</span>
                <span
                  className="inline-block w-2 h-2 rounded-full bg-warning shrink-0"
                  aria-hidden="true"
                />
                <strong
                  className="max-w-36 sm:max-w-56 lg:max-w-xs truncate font-medium"
                  title={beforeName}
                >
                  {beforeName}
                </strong>
                <span className="text-warning shrink-0">→</span>
                <span
                  className="inline-block w-2 h-2 rounded-full bg-primary shrink-0"
                  aria-hidden="true"
                />
                <strong
                  className="max-w-36 sm:max-w-56 lg:max-w-xs truncate font-medium"
                  title={afterName}
                >
                  {afterName || "Nuværende"}
                </strong>
              </span>

              {/* Step navigation — only when there are multiple steps */}
              {totalSteps > 1 && (
                <>
                  <span className="text-muted-foreground hidden sm:inline">
                    |
                  </span>
                  <div
                    className="flex items-center gap-1 shrink-0"
                    title={
                      activeStepBeforeInfo && activeStepAfterInfo
                        ? `Mellemversionstrin: ${activeStepBeforeInfo.shortName} → ${activeStepAfterInfo.shortName}`
                        : undefined
                    }
                  >
                    <span className="text-xs text-warning font-medium hidden sm:inline">
                      Versionstrin
                    </span>
                    <button
                      className="p-0.5 rounded hover:bg-warning/20 dark:hover:bg-warning/30 transition-colors text-warning disabled:opacity-40"
                      onClick={goToPreviousStep}
                      disabled={compareStepIndex === 0}
                      aria-label="Forrige versionstrin"
                    >
                      <ChevronLeft className="size-3.5" />
                    </button>
                    <span className="text-xs text-warning tabular-nums whitespace-nowrap">
                      {compareStepIndex + 1}/{totalSteps}
                    </span>
                    <button
                      className="p-0.5 rounded hover:bg-warning/20 dark:hover:bg-warning/30 transition-colors text-warning disabled:opacity-40"
                      onClick={goToNextStep}
                      disabled={compareStepIndex === totalSteps - 1}
                      aria-label="Næste versionstrin"
                    >
                      <ChevronRight className="size-3.5" />
                    </button>
                  </div>

                  {/* Per-step computing indicator */}
                  {steps[compareStepIndex]?.diffHtml === null &&
                    steps[compareStepIndex]?.error === null && (
                      <span className="flex items-center gap-1 text-xs text-warning">
                        <Loader2 className="size-3 animate-spin" />
                        Beregner trin…
                      </span>
                    )}
                </>
              )}
            </>
          )}

          {/* Mobile: toggle controls row visibility */}
          {!isComputing && !diffError && diffHtml && (
            <button
              className="sm:hidden shrink-0 px-2 py-0.5 rounded text-xs border border-warning/50 dark:border-warning/70 text-warning hover:bg-warning/15 dark:hover:bg-warning/25 transition-colors"
              onClick={() => setControlsExpanded((v) => !v)}
              aria-expanded={controlsExpanded}
            >
              {controlsExpanded ? "Skjul" : "Vis kontroller"}
            </button>
          )}

          {/* Close — always right-aligned */}
          <button
            className="ml-auto shrink-0 hover:opacity-70 transition-opacity text-warning"
            onClick={exitCompareMode}
            aria-label="Afslut sammenligning"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* ── Row 2: controls — view mode + paragraph navigation (only when diff is ready) ── */}
        {/* On small screens, collapse into a toggled row; always visible on sm+ */}
        {!isComputing && !diffError && diffHtml && (
          <div
            className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5 border-t border-warning/30 dark:border-warning/40 ${controlsExpanded ? "flex" : "hidden sm:flex"}`}
          >
            {/* View mode pill group */}
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-xs text-warning/80 font-medium">Vis:</span>
              {/* Connected pill group */}
              <div className="flex rounded-md overflow-hidden border border-warning/50 dark:border-warning/70">
                {/* Ældre tekst — older version with removed passages highlighted */}
                <button
                  className={`${viewBtnBase} border-r border-warning/50 dark:border-warning/70 ${
                    diffFilter === "before"
                      ? "bg-warning text-warning-foreground font-medium"
                      : "text-warning-foreground hover:bg-warning/15 dark:hover:bg-warning/25"
                  }`}
                  onClick={() => setDiffFilter("before")}
                  aria-pressed={diffFilter === "before"}
                  title={`${beforeName} — ${deletions} fjernede passager markeret med pink`}
                >
                  Ældre tekst
                </button>

                {/* Ændringer — inline diff, the most information-dense view */}
                <button
                  className={`${viewBtnBase} border-r border-warning/50 dark:border-warning/70 ${
                    diffFilter === "diff"
                      ? "bg-warning text-warning-foreground font-medium"
                      : "text-warning-foreground hover:bg-warning/15 dark:hover:bg-warning/25"
                  }`}
                  onClick={() => setDiffFilter("diff")}
                  aria-pressed={diffFilter === "diff"}
                  title="Inline diff — tilføjede og fjernede passager markeret side om side i teksten"
                >
                  Ændringer
                  {allChangesCount > 0 && (
                    <span className="ml-1 opacity-70">({allChangesCount})</span>
                  )}
                </button>

                {/* Nyere tekst — current version with added passages highlighted */}
                <button
                  className={`${viewBtnBase} ${
                    diffFilter === "after"
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-warning-foreground hover:bg-warning/15 dark:hover:bg-warning/25"
                  }`}
                  onClick={() => setDiffFilter("after")}
                  aria-pressed={diffFilter === "after"}
                  title={`${afterName || "Nuværende version"} — ${insertions} tilføjede passager markeret med grønt`}
                >
                  Nyere tekst
                </button>
              </div>
            </div>

            {/* Paragraph navigation with explicit label */}
            {totalChanges > 0 && (
              <>
                <span className="text-muted-foreground hidden sm:inline select-none">
                  |
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs text-warning/80 hidden sm:inline">
                    Afsnit med ændring:
                  </span>
                  <button
                    className="p-0.5 rounded hover:bg-warning/20 dark:hover:bg-warning/30 transition-colors text-warning disabled:opacity-40"
                    onClick={diffGoToPrevious}
                    disabled={totalChanges === 0}
                    aria-label="Forrige afsnit med ændring"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        diffGoToPrevious();
                      }
                    }}
                  >
                    <ChevronUp className="size-3.5" />
                  </button>
                  <span className="text-xs text-warning tabular-nums min-w-12 text-center">
                    {diffCurrentIndex + 1}/{totalChanges}
                  </span>
                  <button
                    className="p-0.5 rounded hover:bg-warning/20 dark:hover:bg-warning/30 transition-colors text-warning disabled:opacity-40"
                    onClick={diffGoToNext}
                    disabled={totalChanges === 0}
                    aria-label="Næste afsnit med ændring"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        diffGoToNext();
                      }
                    }}
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                </div>
              </>
            )}

            {/* Diff legend — only in Ændringer view, far right on large screens */}
            {diffFilter === "diff" && (
              <span className="hidden lg:flex items-center gap-2 text-xs text-warning/70 ml-auto">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(142_71%_45%/0.35)] border border-[hsl(142_71%_40%/0.5)]" />
                  Tilføjet
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(0_84%_60%/0.25)] border border-[hsl(0_84%_55%/0.5)]" />
                  Fjernet
                </span>
              </span>
            )}
          </div>
        )}

        {/* ── Row 3: readability hint — nudge users toward clean views in dense diffs ── */}
        {!isComputing &&
          !diffError &&
          diffHtml &&
          diffFilter === "diff" &&
          allChangesCount > 5 &&
          !hintDismissed && (
            <div
              id={hintId}
              className="flex items-start gap-2 px-3 py-1.5 border-t border-warning/40 dark:border-warning/40 bg-warning/5 dark:bg-warning/10 text-warning/90 dark:text-warning-foreground/80 text-xs"
            >
              <Info className="size-3 mt-0.5 shrink-0 opacity-70" />
              <span>
                Brug{" "}
                <button
                  className="underline underline-offset-2 hover:opacity-100 transition-opacity"
                  onClick={() => setDiffFilter("before")}
                >
                  Ældre tekst
                </button>{" "}
                eller{" "}
                <button
                  className="underline underline-offset-2 hover:opacity-100 transition-opacity"
                  onClick={() => setDiffFilter("after")}
                >
                  Nyere tekst
                </button>{" "}
                for at læse uden diff-markeringer. Fold et markeret afsnit ud
                med "Vis begge versioner" for direkte sammenligning.
              </span>
              <button
                className="ml-auto shrink-0 hover:opacity-100 opacity-50 transition-opacity"
                onClick={() => setHintDismissed(true)}
                aria-label="Luk tip"
              >
                <X className="size-3" />
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

// ─── LegislationDocumentContent ───────────────────────────────────────────────

interface LegislationDocumentContentProps {
  legislation: Legislation | null;
  documentId: string | undefined;
  datasource: string | undefined;
  rawCSS: string;
  rawHTML: string;
  loading: boolean;
  error: string | null;
  isDocumentLoading?: boolean;
  timelineItems: TimelineItem[] | undefined;
  isTimelineReady?: boolean;
  highlightIds: string[];
  scrollToId: string | undefined;
  highlightMatchText?: string;
  highlightChunkText?: string;
  /** Prefills the in-document search, e.g. the term from a global search result. */
  documentSearchSeedTerm?: string;
  isProcessingIncomplete?: boolean;
  isConsolidatedRender?: boolean;
  isConsolidationLoading?: boolean;
  isConsolidationNotFound?: boolean;
  consolidationAsOf?: string | null;
  /** Coverage date (t.o.m.) — max activation date of incorporated change laws. */
  consolidationResolvedAsOf?: string | null;
  consolidationRenderContext?: ConsolidationRenderContext | null;
  changedContainerIds?: ReadonlySet<string>;
  changedContainerGroupKeys?: ReadonlyMap<string, string>;
  changedContainerStatusById?: ReadonlyMap<string, ConsolidationChangeStatus>;
  deletedContainerChanges?: readonly DeletedContainerChange[];
  consolidationFeatureEnabled?: boolean;
  timelineRootAccession?: string | null;
  showingParentTimeline?: boolean;
  timelineScope?: TimelineScope;
  canSwapTimeline?: boolean;
  parentTimelineItems?: TimelineItem[];
  ownTimelineItems?: TimelineItem[];
  timelineTransitionKey?: string;
  timelineAtParam?: number | null;
  onConsolidationChangeLaw?: (
    changeLaw: string,
    asOf?: string | null,
    timelineAt?: number | null
  ) => void;
  /**
   * Re-renders the active consolidation pinned to the given activation date
   * (keeps change_law so the viewer stays at the same consolidation).
   */
  onConsolidationAsOf?: (asOf: string | null) => void;
}

/**
 * Shared component for rendering legislation document content
 * Used by both LegislationPage and LegislationHTMLReferenceView
 */
const LegislationDocumentContent: React.FC<LegislationDocumentContentProps> = ({
  legislation,
  documentId,
  datasource,
  rawCSS,
  rawHTML,
  loading,
  error,
  isDocumentLoading = false,
  timelineItems,
  isTimelineReady = true,
  highlightIds,
  scrollToId,
  highlightMatchText,
  highlightChunkText,
  documentSearchSeedTerm,
  isProcessingIncomplete = false,
  isConsolidatedRender = false,
  isConsolidationLoading = false,
  isConsolidationNotFound = false,
  consolidationAsOf = null,
  consolidationResolvedAsOf = null,
  consolidationRenderContext = null,
  changedContainerIds = EMPTY_CONTAINER_IDS,
  changedContainerGroupKeys = EMPTY_CONTAINER_GROUP_KEYS,
  changedContainerStatusById = EMPTY_CONTAINER_STATUS_BY_ID,
  deletedContainerChanges = EMPTY_DELETED_CONTAINER_CHANGES,
  consolidationFeatureEnabled = false,
  timelineRootAccession = null,
  showingParentTimeline = false,
  timelineScope = "parent",
  canSwapTimeline = false,
  parentTimelineItems,
  ownTimelineItems,
  timelineTransitionKey,
  timelineAtParam = null,
  onConsolidationChangeLaw,
  onConsolidationAsOf,
}) => {
  const { openNavigation } = useOpenAppNavigation();
  const { useQueryUsersFavoriteDocuments, useQuerySubscriptions } = useUsers();
  const favoriteDocumentsQuery = useQueryUsersFavoriteDocuments();
  const subscriptionsQuery = useQuerySubscriptions();
  const [navigationFading, setNavigationFading] = useState(false);
  const prevDocumentNavigationKeyRef = useRef<string | null>(null);
  // Assigned once the instruction-panel state/refs are declared below; lets the
  // earlier-declared timeline handlers clear review state synchronously without
  // a temporal-dead-zone reference to those later bindings.
  const clearReviewStateRef = useRef<() => void>(() => {});

  const currentSource = getDisplayName(datasource) || datasource || "";
  // Kept renewed: the download button opens it whenever the reader clicks (LAR-542).
  const originalPdf = useLegislationPdfUrl(legislation, { keepFresh: true });

  const favoriteSubscribe = useMemo(() => {
    if (!legislation) return undefined;

    const entityPath = `legislation/${legislation.externalId}/datasource/${legislation.datasource}`;
    const favoriteIds =
      favoriteDocumentsQuery.data?.favoriteLegislations.map(
        (fav) => fav.externalId
      ) ?? [];
    const subscribedEntry = Array.isArray(subscriptionsQuery.data)
      ? subscriptionsQuery.data.find((s) => s.entityPath === entityPath)
      : undefined;

    let documentTypeId: number | undefined;
    if (
      legislation.metadataObject &&
      typeof legislation.metadataObject === "string"
    ) {
      try {
        const parsed = JSON.parse(legislation.metadataObject) as {
          documentTypeId?: string | number;
        };
        const raw = parsed?.documentTypeId;
        if (raw != null) {
          const num = Number(raw);
          documentTypeId = Number.isNaN(num) ? undefined : num;
        }
      } catch {
        documentTypeId = undefined;
      }
    }

    return {
      externalId: legislation.externalId,
      datasource: legislation.datasource,
      title: legislation.title,
      internalId: legislation.internalId,
      legislationExternalId: legislation.legislationExternalId,
      publishedOn: legislation.publishedOn,
      isCurrent: legislation.isCurrent,
      documentTypeId,
      isFavorite: favoriteIds.includes(legislation.externalId),
      isSubscribed: !!subscribedEntry,
      legislationSubscriptionId: subscribedEntry?.id,
      isReady: favoriteDocumentsQuery.isFetched && subscriptionsQuery.isFetched,
    };
  }, [
    legislation,
    favoriteDocumentsQuery.data,
    favoriteDocumentsQuery.isFetched,
    subscriptionsQuery.data,
    subscriptionsQuery.isFetched,
  ]);

  const retsinformationMeta = useMemo(() => {
    if (!legislation) return null;
    return getRetsinformationMetadataFromLegislation(legislation);
  }, [legislation]);

  const inboundChanges = useMemo(
    () => retsinformationMeta?.inboundChanges ?? [],
    [retsinformationMeta]
  );

  const anchorLawAccession = useMemo(
    () => resolveAnchorLawAccessionForTimeline(documentId, retsinformationMeta),
    [documentId, retsinformationMeta]
  );

  const compareCurrentAccession = useMemo(() => {
    if (
      consolidationFeatureEnabled &&
      anchorLawAccession &&
      consolidationRenderContext?.changeLaw &&
      isConsolidatedRender &&
      !isConsolidationNotFound
    ) {
      return consolidationRenderContext.changeLaw;
    }
    return documentId ?? legislation?.externalId;
  }, [
    anchorLawAccession,
    consolidationFeatureEnabled,
    consolidationRenderContext?.changeLaw,
    documentId,
    isConsolidatedRender,
    isConsolidationNotFound,
    legislation?.externalId,
  ]);

  // Normalize the delivered BEK tree so amendment BEKs render inline on their
  // anchor's branch and only genuine legal splits branch. All tree/flat/index
  // consumers below use this so navigation, indices, and diamonds stay in sync.
  const normalizedTimelineItems = useMemo(
    () => normalizeTimelineItems(timelineItems),
    [timelineItems]
  );

  const isConsolidationComparisonEnabled =
    consolidationFeatureEnabled &&
    Boolean(anchorLawAccession) &&
    isConsolidationTimelineComparisonAvailable(
      normalizedTimelineItems,
      anchorLawAccession,
      inboundChanges,
      compareCurrentAccession
    );

  const isComparisonEnabled = isConsolidationComparisonEnabled;

  // Context needed to file per-element customer feedback against the displayed
  // consolidation snapshot. Null unless we are showing a resolved consolidation.
  const consolidationFeedbackContext = useMemo(() => {
    if (!isConsolidatedRender || isConsolidationNotFound) return null;
    if (!consolidationRenderContext) return null;
    const asOf = consolidationAsOf ?? consolidationRenderContext.asOf;
    if (!asOf) return null;
    return {
      lawExternalId: consolidationRenderContext.lawExternalId,
      asOf,
      view: consolidationRenderContext.view,
      changedContainerIds,
    };
  }, [
    isConsolidatedRender,
    isConsolidationNotFound,
    consolidationRenderContext,
    consolidationAsOf,
    changedContainerIds,
  ]);

  // Consolidation identity for references created from the current view. Uses the
  // URL-level `asOf` (not the resolved coverage date) so following the link
  // reproduces the exact render the reference was created from.
  const consolidationLinkContext = useMemo(() => {
    if (!isConsolidatedRender || isConsolidationNotFound) return null;
    return consolidationRenderContext;
  }, [
    isConsolidatedRender,
    isConsolidationNotFound,
    consolidationRenderContext,
  ]);

  const hasChangedContainers = changedContainerIds.size > 0;

  const containerChangeFetchParams = useMemo(() => {
    if (!consolidationFeedbackContext) return null;
    return {
      lawExternalId: consolidationFeedbackContext.lawExternalId,
      asOf: consolidationFeedbackContext.asOf,
      view: consolidationFeedbackContext.view,
      changeLaw: consolidationRenderContext?.changeLaw,
    };
  }, [consolidationFeedbackContext, consolidationRenderContext?.changeLaw]);

  // Compare targets are restricted to a single LBK window (the anchor LBK plus
  // its consolidations) on the consolidation path.
  const eligibleCompareAccessions = useMemo(() => {
    if (!isConsolidationComparisonEnabled) return null;
    return getCompareWindowAccessions(
      normalizedTimelineItems,
      anchorLawAccession,
      inboundChanges
    );
  }, [
    anchorLawAccession,
    inboundChanges,
    isConsolidationComparisonEnabled,
    normalizedTimelineItems,
  ]);

  const consolidationComparisonOptions = useMemo(() => {
    if (!isConsolidationComparisonEnabled || !anchorLawAccession) return null;
    return {
      anchorLawExternalId: anchorLawAccession,
      inboundChanges,
      view: consolidationRenderContext?.view ?? ("draft" as ConsolidationView),
      isConsolidatedRender,
    };
  }, [
    anchorLawAccession,
    consolidationRenderContext?.view,
    inboundChanges,
    isConsolidatedRender,
    isConsolidationComparisonEnabled,
  ]);

  // Comparison mode (M-23)
  const {
    isCompareMode,
    compareTargetInfo,
    activeStepBeforeInfo,
    activeStepAfterInfo,
    diffHtml,
    isComputing,
    fetchedCount,
    totalToFetch,
    diffError,
    steps,
    currentStepIndex: compareStepIndex,
    totalSteps,
    toggleCompareMode,
    exitCompareMode,
    selectCompareTarget,
    goToNextStep,
    goToPreviousStep,
    activeStepBeforeHtml,
    activeStepAfterHtml,
  } = useLegislationComparison({
    currentProcessedHtml: rawHTML,
    currentExternalId: compareCurrentAccession,
    datasource,
    timelineItems: normalizedTimelineItems,
    consolidationComparison: consolidationComparisonOptions,
  });

  // When document changes (navigation), exit comparison mode
  useEffect(() => {
    exitCompareMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  useEffect(() => {
    exitCompareMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidationRenderContext?.changeLaw, consolidationRenderContext?.asOf]);

  // Handle timeline navigation
  const outboundChanges = useMemo(
    () => retsinformationMeta?.outboundChanges ?? [],
    [retsinformationMeta]
  );

  const timelineFamilyRoot = useMemo(
    () => resolveTimelineFamilyRoot(normalizedTimelineItems) ?? undefined,
    [normalizedTimelineItems]
  );

  const { data: timelineWindowMetadata } = useConsolidationTimelineMetadata({
    rootExternalId: timelineFamilyRoot,
    timelineFallbackExternalId: documentId ?? undefined,
    enabled: consolidationFeatureEnabled && Boolean(timelineFamilyRoot),
  });

  // Inbound changes keyed by window anchor, so diamonds render across all LBK
  // windows. Only fall back to the currently-loaded anchor's silver metadata
  // while the window-metadata endpoint has not loaded yet. Once it has, an anchor
  // absent from byAnchor means its window has no live inbound pairs (the service
  // omits empty windows), so injecting silver would show stale approval state
  // that list/search — which treat empty live enrichment as empty — do not.
  const inboundByAnchor = useMemo(() => {
    const map = new Map<string, InboundChange[]>(
      timelineWindowMetadata?.byAnchor ?? []
    );
    if (
      !timelineWindowMetadata &&
      anchorLawAccession &&
      inboundChanges.length &&
      !map.has(anchorLawAccession)
    ) {
      map.set(anchorLawAccession, inboundChanges);
    }
    return map;
  }, [timelineWindowMetadata, anchorLawAccession, inboundChanges]);

  const timelineAmendmentCountAfter = useMemo(() => {
    if (legislation?.timelineAmendmentCountAfter != null) {
      return legislation.timelineAmendmentCountAfter;
    }
    const anchorAccession = anchorLawAccession ?? documentId;
    return countConsolidationAmendmentsAfter(
      normalizedTimelineItems,
      anchorAccession,
      retsinformationMeta?.documentTypeId
    );
  }, [
    anchorLawAccession,
    documentId,
    legislation?.timelineAmendmentCountAfter,
    normalizedTimelineItems,
    retsinformationMeta?.documentTypeId,
  ]);

  const anchorConsolidationProgress = useMemo(() => {
    if (!consolidationFeatureEnabled || !anchorLawAccession) return null;
    // Numerator from live window metadata (matches the timeline diamonds); the silver
    // inbound is only a fallback while live status has not loaded. Once it has, an
    // absent anchor means an empty window — use [] rather than stale silver so the
    // viewer matches list/search.
    const liveInbound =
      inboundByAnchor.get(anchorLawAccession) ??
      (timelineWindowMetadata ? [] : retsinformationMeta?.inboundChanges);
    return getConsolidationProgressDisplay(
      {
        inboundChanges: liveInbound,
        timelineAmendmentCountAfter,
        documentTypeId: retsinformationMeta?.documentTypeId,
      },
      {
        timelineItems: normalizedTimelineItems,
        anchorAccession: anchorLawAccession,
        documentId,
      }
    );
  }, [
    anchorLawAccession,
    consolidationFeatureEnabled,
    inboundByAnchor,
    timelineWindowMetadata,
    retsinformationMeta?.documentTypeId,
    retsinformationMeta?.inboundChanges,
    timelineAmendmentCountAfter,
    normalizedTimelineItems,
    documentId,
  ]);

  const showTimelineConsolidationProgress = Boolean(
    anchorConsolidationProgress
  );

  // Diamonds render whenever we have window metadata for any window, even when
  // the loaded document is not itself an anchor LBK.
  const showConsolidationDiamonds =
    consolidationFeatureEnabled &&
    (inboundByAnchor.size > 0 || showTimelineConsolidationProgress);

  const latchedFamilyRootRef = useRef<string | undefined>(undefined);
  const [consolidationDiamondsLatched, setConsolidationDiamondsLatched] =
    useState(false);

  useEffect(() => {
    if (latchedFamilyRootRef.current !== timelineFamilyRoot) {
      latchedFamilyRootRef.current = timelineFamilyRoot;
      setConsolidationDiamondsLatched(false);
    }
  }, [timelineFamilyRoot]);

  useEffect(() => {
    if (showConsolidationDiamonds) {
      setConsolidationDiamondsLatched(true);
    }
  }, [showConsolidationDiamonds]);

  const showConsolidationDiamondsEffective =
    showConsolidationDiamonds || consolidationDiamondsLatched;

  const timelineUniverseRoot = useMemo(() => {
    if (showingParentTimeline && timelineRootAccession) {
      return timelineRootAccession;
    }
    if (!timelineItems?.length) return documentId;
    const first = timelineItems[0];
    if (first.children?.length) return first.accessionNumber ?? documentId;
    return documentId;
  }, [documentId, showingParentTimeline, timelineItems, timelineRootAccession]);

  const handleTimelineNavigate = useCallback(
    (
      accessionNumber: string,
      event?: React.MouseEvent,
      flatTimelineIndex?: number
    ) => {
      if (!datasource) {
        return;
      }

      const isInTimelineUniverse = Boolean(
        timelineRootAccession &&
        documentId &&
        timelineRootAccession !== documentId
      );

      // Only skip timeline_root when explicitly viewing own scope inside a universe.
      if (isInTimelineUniverse && timelineScope === "own") {
        openNavigation(
          resolveTimelineNavigationTarget({
            targetAccessionNumber: accessionNumber,
            datasource,
            flatTimelineIndex,
          }),
          event
        );
        return;
      }

      const isTimelineUniverseHop =
        isInTimelineUniverse &&
        Boolean(timelineUniverseRoot) &&
        accessionNumber !== timelineUniverseRoot;

      openNavigation(
        resolveTimelineNavigationTarget({
          targetAccessionNumber: accessionNumber,
          datasource,
          timelineRootAccession: timelineUniverseRoot ?? undefined,
          flatTimelineIndex,
        }),
        event,
        isTimelineUniverseHop ? { navigationMode: "replace" } : undefined
      );
    },
    [
      datasource,
      documentId,
      openNavigation,
      timelineRootAccession,
      timelineScope,
      timelineUniverseRoot,
    ]
  );

  const flatTimelineItems = useMemo(
    () => flattenTimelineItems(normalizedTimelineItems),
    [normalizedTimelineItems]
  );

  const handleOpenConsolidationFromTimeline = useCallback(
    (
      changelawAccession: string,
      options: {
        asOf: string | null;
        flatTimelineIndex: number;
      },
      event?: React.MouseEvent
    ) => {
      // Resolve the clicked node's own window anchor so diamonds in any window
      // open the right consolidation, not just the loaded anchor LBK's window.
      const windowAnchor =
        resolveWindowAnchorForFlatIndex(
          flatTimelineItems,
          options.flatTimelineIndex
        ) ?? anchorLawAccession;
      if (!windowAnchor) return;

      // Close any open amendment review synchronously before the consolidation
      // changes, so no render cycle runs with the new consolidation context and
      // a stale reviewed container / scroll anchor (LAR-306).
      clearReviewStateRef.current();

      if (documentId === windowAnchor && onConsolidationChangeLaw) {
        onConsolidationChangeLaw(
          changelawAccession,
          options.asOf,
          options.flatTimelineIndex
        );
        return;
      }

      openNavigation(
        {
          kind: "path",
          path: buildConsolidationViewerPath(windowAnchor, {
            changeLaw: changelawAccession,
            asOf: options.asOf ?? undefined,
            timelineRoot: showingParentTimeline
              ? (timelineUniverseRoot ?? undefined)
              : undefined,
            timelineAt: options.flatTimelineIndex,
          }),
        },
        event
      );
    },
    [
      anchorLawAccession,
      flatTimelineItems,
      documentId,
      onConsolidationChangeLaw,
      openNavigation,
      timelineUniverseRoot,
      showingParentTimeline,
    ]
  );

  const handleNavigateToChangeLaw = useCallback(
    (changeLawExternalId: string) => {
      const matchingIndices = findFlatTimelineIndicesForAccession(
        flatTimelineItems,
        changeLawExternalId
      );
      if (matchingIndices.length === 0) return;

      const anchor =
        (isConsolidatedRender &&
          !isConsolidationNotFound &&
          consolidationRenderContext?.lawExternalId) ||
        anchorLawAccession ||
        documentId;
      let flatTimelineIndex = matchingIndices[0];
      if (matchingIndices.length > 1 && anchor) {
        const inWindow = matchingIndices.find(
          (index) =>
            resolveWindowAnchorForFlatIndex(flatTimelineItems, index) === anchor
        );
        if (inWindow != null) {
          flatTimelineIndex = inWindow;
        }
      }

      setInstructionContainer(null);
      handleTimelineNavigate(changeLawExternalId, undefined, flatTimelineIndex);
    },
    [
      anchorLawAccession,
      consolidationRenderContext?.lawExternalId,
      documentId,
      flatTimelineItems,
      handleTimelineNavigate,
      isConsolidatedRender,
      isConsolidationNotFound,
    ]
  );

  const activeFlatTimelineIndex = useMemo(() => {
    const targetAccession =
      consolidationFeatureEnabled &&
      isConsolidatedRender &&
      !isConsolidationNotFound &&
      consolidationRenderContext?.changeLaw
        ? consolidationRenderContext.changeLaw
        : documentId;

    if (timelineAtParam != null) {
      const pinned = flatTimelineItems[timelineAtParam];
      if (
        pinned &&
        targetAccession &&
        pinned.accessionNumber === targetAccession
      ) {
        return timelineAtParam;
      }
    }

    return resolveDefaultFlatTimelineIndex(flatTimelineItems, targetAccession);
  }, [
    consolidationFeatureEnabled,
    consolidationRenderContext?.changeLaw,
    documentId,
    flatTimelineItems,
    isConsolidatedRender,
    isConsolidationNotFound,
    timelineAtParam,
  ]);

  const timelineSelection = useMemo((): TimelineSelection | null => {
    if (!documentId) return null;
    if (
      consolidationFeatureEnabled &&
      isConsolidatedRender &&
      !isConsolidationNotFound &&
      consolidationRenderContext?.changeLaw
    ) {
      return {
        kind: "consolidation",
        accessionNumber: consolidationRenderContext.changeLaw,
        ...(activeFlatTimelineIndex != null
          ? { flatTimelineIndex: activeFlatTimelineIndex }
          : {}),
      };
    }
    return {
      kind: "silver",
      accessionNumber: documentId,
      ...(activeFlatTimelineIndex != null
        ? { flatTimelineIndex: activeFlatTimelineIndex }
        : {}),
    };
  }, [
    activeFlatTimelineIndex,
    consolidationFeatureEnabled,
    consolidationRenderContext?.changeLaw,
    documentId,
    isConsolidatedRender,
    isConsolidationNotFound,
  ]);

  const timelineScrollAccession =
    timelineSelection?.accessionNumber ?? documentId ?? "";

  const documentNavigationKey = useMemo(
    () =>
      [
        timelineTransitionKey ?? "",
        documentId ?? "",
        timelineAtParam ?? "",
        consolidationRenderContext?.changeLaw ?? "",
        consolidationRenderContext?.asOf ?? "",
        consolidationRenderContext?.view ?? "",
      ].join("|"),
    [
      timelineAtParam,
      consolidationRenderContext?.asOf,
      consolidationRenderContext?.changeLaw,
      consolidationRenderContext?.view,
      documentId,
      timelineTransitionKey,
    ]
  );

  useEffect(() => {
    setInstructionContainer(null);
  }, [documentNavigationKey]);

  const showVerifiedBanner =
    consolidationFeatureEnabled &&
    isConsolidatedRender &&
    !isConsolidationNotFound &&
    (consolidationRenderContext?.view ?? "draft") === "verified";

  // Check if paragraph navigation should be enabled for this source
  const paragraphExtractionMode =
    isConsolidatedRender && !isConsolidationNotFound
      ? "consolidated"
      : "silver";
  const showParagraphNavigation =
    PARAGRAPH_NAVIGATION_SUPPORTED_SOURCES.includes(currentSource) &&
    Boolean(rawHTML?.trim());

  // Ref to the container that holds the DocumentRenderer
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickyToolbarRef = useRef<HTMLDivElement | null>(null);
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0);
  // Ref to the actual doc-root element inside DocumentRenderer
  const docRootRef = useRef<HTMLElement | null>(null);
  // Track if doc-root has been found
  const [docRootReady, setDocRootReady] = useState(false);

  // Zoom state management
  const [zoomLevel, setZoomLevel] = useState(100);
  const [highlightConsolidationChanges, setHighlightConsolidationChanges] =
    useState(false);
  const [instructionContainer, setInstructionContainer] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [viewerColumnEl, setViewerColumnEl] = useState<HTMLElement | null>(
    null
  );

  const instructionPanelOpen = instructionContainer !== null;

  const instructionChangeParams = useMemo(() => {
    if (
      !instructionPanelOpen ||
      !instructionContainer ||
      !containerChangeFetchParams
    ) {
      return null;
    }
    return {
      ...containerChangeFetchParams,
      containerId: instructionContainer.id,
    };
  }, [instructionPanelOpen, instructionContainer, containerChangeFetchParams]);

  const {
    data: instructionChangeData,
    isLoading: isInstructionLoading,
    isError: isInstructionError,
    diffHtml: containerDiffHtml,
    isComputingDiff,
    diffComputeError,
    cannotHighlightDiff,
    isFullInsertion,
    isFullDeletion,
  } = useContainerAmendmentDiff(instructionChangeParams, instructionPanelOpen);

  const containerAmendmentReview = useMemo(() => {
    if (!instructionContainer) return null;
    return {
      containerId: instructionContainer.id,
      diffHtml: containerDiffHtml,
    };
  }, [instructionContainer, containerDiffHtml]);

  const hasPermission = useHasPermission();
  const hasFeedbackPermission = hasPermission({
    allOf: [Permission.CreateConsolidationFeedback],
  });

  const instructionPanelFeedback = useMemo(() => {
    if (
      !consolidationFeedbackContext ||
      !hasFeedbackPermission ||
      !instructionContainer ||
      isCompareMode
    ) {
      return null;
    }
    return {
      lawExternalId: consolidationFeedbackContext.lawExternalId,
      asOf: consolidationFeedbackContext.asOf,
      view: consolidationFeedbackContext.view,
      containerId: instructionContainer.id,
    };
  }, [
    consolidationFeedbackContext,
    hasFeedbackPermission,
    instructionContainer,
    isCompareMode,
  ]);

  const { open: rightSidebarOpen, setOpen: setRightSidebarOpen } =
    useSidebar("right");
  const setRightSidebarToggleDisabled = useSidebarStore(
    (state) => state.setRightSidebarToggleDisabled
  );
  const rightSidebarOpenBeforeReviewRef = useRef<boolean | null>(null);
  const instructionContainerRef = useRef(instructionContainer);
  instructionContainerRef.current = instructionContainer;

  const instructionScrollAnchorRef = useRef<{
    containerId: string;
    targetViewportOffset: number;
  } | null>(null);
  const pendingCloseScrollRestoreRef = useRef<{
    containerId: string;
    targetViewportOffset: number;
  } | null>(null);
  const consolidationNavPendingScrollRef = useRef<string | null>(null);

  useEffect(() => {
    pendingCloseScrollRestoreRef.current = null;
  }, [documentNavigationKey]);

  const captureContainerViewportOffset = useCallback(
    (docRoot: HTMLElement, containerId: string): number | null => {
      const scroller =
        findScrollContainer(docRoot) ||
        document.querySelector<HTMLElement>('[data-content-scroll="true"]') ||
        document.documentElement;
      const el = docRoot.querySelector<HTMLElement>(
        `#${CSS.escape(containerId)}`
      );
      if (!el) return null;
      const scrollerRect = scroller.getBoundingClientRect();
      return el.getBoundingClientRect().top - scrollerRect.top;
    },
    []
  );

  const scheduleViewportAnchorRestore = useCallback(
    (containerId: string, targetViewportOffset: number) => {
      const docRoot = docRootRef.current;
      if (!docRoot) return;

      const scroller =
        findScrollContainer(docRoot) ||
        document.querySelector<HTMLElement>('[data-content-scroll="true"]') ||
        document.documentElement;

      const restore = (attempt: number) => {
        const el = docRoot.querySelector<HTMLElement>(
          `#${CSS.escape(containerId)}`
        );
        if (!el) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const newViewportOffset =
          el.getBoundingClientRect().top - scrollerRect.top;
        const correction = newViewportOffset - targetViewportOffset;
        if (Math.abs(correction) > 1) {
          scroller.scrollTop += correction;
        }

        if (attempt < 4) {
          requestAnimationFrame(() => restore(attempt + 1));
        }
      };

      requestAnimationFrame(() => restore(0));
    },
    []
  );

  const handleCloseInstructionPanel = useCallback(() => {
    const closingId = instructionContainerRef.current?.id;
    const docRoot = docRootRef.current;

    let targetViewportOffset: number | null = null;
    if (closingId && docRoot) {
      targetViewportOffset = captureContainerViewportOffset(docRoot, closingId);
    }

    instructionScrollAnchorRef.current = null;
    pendingCloseScrollRestoreRef.current =
      closingId && targetViewportOffset !== null
        ? { containerId: closingId, targetViewportOffset }
        : null;
    setInstructionContainer(null);
  }, [captureContainerViewportOffset]);

  // Hard-close the amendment review with no scroll restoration — used when the
  // consolidation itself is about to change, so leftover review state cannot
  // bleed into the next consolidation.
  const clearInstructionReviewState = useCallback(() => {
    instructionScrollAnchorRef.current = null;
    pendingCloseScrollRestoreRef.current = null;
    consolidationNavPendingScrollRef.current = null;
    setInstructionContainer(null);
  }, []);
  clearReviewStateRef.current = clearInstructionReviewState;

  const handleChangedContainerClick = useCallback(
    (container: { id: string; type: string }) => {
      const docRoot = docRootRef.current;
      const targetViewportOffset = docRoot
        ? captureContainerViewportOffset(docRoot, container.id)
        : null;

      setHighlightConsolidationChanges(true);
      pendingCloseScrollRestoreRef.current = null;
      setInstructionContainer(container);

      if (targetViewportOffset === null) return;

      instructionScrollAnchorRef.current = {
        containerId: container.id,
        targetViewportOffset,
      };

      scheduleViewportAnchorRestore(container.id, targetViewportOffset);
    },
    [captureContainerViewportOffset, scheduleViewportAnchorRestore]
  );

  useEffect(() => {
    if (instructionPanelOpen) {
      const anchor = instructionScrollAnchorRef.current;
      if (!anchor || instructionContainer?.id !== anchor.containerId) return;

      scheduleViewportAnchorRestore(
        anchor.containerId,
        anchor.targetViewportOffset
      );
      return;
    }

    const pending = pendingCloseScrollRestoreRef.current;
    if (!pending) return;

    scheduleViewportAnchorRestore(
      pending.containerId,
      pending.targetViewportOffset
    );

    if (
      rightSidebarOpenBeforeReviewRef.current === null &&
      !isComputingDiff &&
      !containerDiffHtml
    ) {
      pendingCloseScrollRestoreRef.current = null;
    }
  }, [
    containerDiffHtml,
    instructionContainer?.id,
    instructionPanelOpen,
    isComputingDiff,
    rightSidebarOpen,
    scheduleViewportAnchorRestore,
  ]);

  useEffect(() => {
    const pendingId = consolidationNavPendingScrollRef.current;
    if (
      !instructionPanelOpen ||
      !pendingId ||
      instructionContainer?.id !== pendingId
    ) {
      return;
    }
    if (isComputingDiff) return;

    const docRoot = docRootRef.current;
    if (!docRoot) return;

    const el = docRoot.querySelector<HTMLElement>(`#${CSS.escape(pendingId)}`);
    if (!el) return;

    scrollChangedContainerIntoView(docRoot, el);
    consolidationNavPendingScrollRef.current = null;
  }, [
    containerDiffHtml,
    instructionContainer?.id,
    instructionPanelOpen,
    isComputingDiff,
  ]);

  useEffect(() => {
    if (instructionPanelOpen) {
      if (rightSidebarOpenBeforeReviewRef.current === null) {
        rightSidebarOpenBeforeReviewRef.current = rightSidebarOpen;
      }
      if (rightSidebarOpen) {
        setRightSidebarOpen(false);
      }
      return;
    }

    if (rightSidebarOpenBeforeReviewRef.current !== null) {
      const restoreOpen = rightSidebarOpenBeforeReviewRef.current;
      rightSidebarOpenBeforeReviewRef.current = null;
      if (restoreOpen !== rightSidebarOpen) {
        setRightSidebarOpen(restoreOpen);
      } else if (!isComputingDiff && !containerDiffHtml) {
        // Sidebar unchanged — no re-render; clear only after diff panel has unmounted.
        pendingCloseScrollRestoreRef.current = null;
      }
    }
  }, [
    containerDiffHtml,
    instructionPanelOpen,
    isComputingDiff,
    rightSidebarOpen,
    setRightSidebarOpen,
  ]);

  useEffect(() => {
    setRightSidebarToggleDisabled(instructionPanelOpen);
    return () => setRightSidebarToggleDisabled(false);
  }, [instructionPanelOpen, setRightSidebarToggleDisabled]);

  useEffect(() => {
    if (!instructionPanelOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleCloseInstructionPanel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [instructionPanelOpen, handleCloseInstructionPanel]);

  useEffect(() => {
    const toolbar = stickyToolbarRef.current;
    if (!toolbar) return;

    const updateHeight = () => {
      setStickyToolbarHeight(toolbar.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [
    showVerifiedBanner,
    hasChangedContainers,
    isCompareMode,
    compareTargetInfo,
    isConsolidatedRender,
    isConsolidationNotFound,
  ]);

  useEffect(() => {
    setZoomLevel(100);
    setDocRootReady(false);
  }, [documentId]);

  // Calculate zoom level to fit content to container width
  const handleFitToWidth = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth || 800;
    // Domsdatabasen content is designed for 1024px width
    const contentWidth = 1024;
    // Calculate scale to fit, with some padding (subtract padding from container)
    const availableWidth = containerWidth - 32; // Account for padding
    const scale = Math.min(
      100,
      Math.floor((availableWidth / contentWidth) * 100)
    );
    setZoomLevel(Math.max(50, scale)); // Don't go below 50%
  }, []);

  // Find the doc-root element after render
  useEffect(() => {
    if (containerRef.current) {
      const docRoot = containerRef.current.querySelector(
        ".doc-root"
      ) as HTMLElement | null;
      if (docRoot) {
        docRootRef.current = docRoot;
        setDocRootReady(true);
      }
    }
  }, [rawHTML]);

  useEffect(() => {
    if (prevDocumentNavigationKeyRef.current === null) {
      prevDocumentNavigationKeyRef.current = documentNavigationKey;
      return;
    }
    if (prevDocumentNavigationKeyRef.current === documentNavigationKey) return;
    prevDocumentNavigationKeyRef.current = documentNavigationKey;

    setNavigationFading(true);
    const id = window.setTimeout(() => setNavigationFading(false), 200);

    const scroller =
      (docRootRef.current && findScrollContainer(docRootRef.current)) ||
      document.querySelector<HTMLElement>('[data-content-scroll="true"]') ||
      document.documentElement;
    scroller.scrollTo({ top: 0, behavior: "auto" });

    return () => window.clearTimeout(id);
  }, [documentNavigationKey]);

  const paragraphItems = useParagraphExtraction(
    rawHTML,
    showParagraphNavigation,
    paragraphExtractionMode
  );

  // Documents without § structure (vejledninger, cirkulærer, skrivelser) navigate
  // by their numbered section outline instead (LAR-126).
  const jumpNavigation = useMemo(
    () =>
      resolveDocumentJumpNavigation({
        paragraphItems,
        tocItems: legislation?.tableOfContentsGold,
        isConsolidatedRender: isConsolidatedRender && !isConsolidationNotFound,
      }),
    [
      paragraphItems,
      legislation?.tableOfContentsGold,
      isConsolidatedRender,
      isConsolidationNotFound,
    ]
  );

  const showHistoricWatermark = useMemo(
    () =>
      shouldShowConsolidationHistoricWatermark({
        isConsolidatedRender,
        isConsolidationNotFound,
        anchorLawAccession: consolidationRenderContext?.lawExternalId,
        documentExternalId: documentId ?? legislation?.externalId,
        legislationIsCurrent: legislation?.isCurrent,
        timelineItems,
      }),
    [
      consolidationRenderContext?.lawExternalId,
      documentId,
      isConsolidatedRender,
      isConsolidationNotFound,
      legislation?.externalId,
      legislation?.isCurrent,
      timelineItems,
    ]
  );

  const documentIsCurrent = !showHistoricWatermark;

  const showOutboundPanel =
    consolidationFeatureEnabled &&
    outboundChanges.length > 0 &&
    Boolean(legislation?.externalId);

  // Short name for the current (newer) version — used in version-aware diff toggle labels.
  // Primary: match timeline item by accessionNumber === legislation.externalId.
  // Fallback: isCurrentDocument flag (may not always be set).
  const currentVersionShortName = useMemo(() => {
    if (!flatTimelineItems.length) return null;
    const currentAccession =
      isConsolidationComparisonEnabled && consolidationRenderContext?.changeLaw
        ? consolidationRenderContext.changeLaw
        : legislation?.externalId;
    return (
      flatTimelineItems.find((i) => i.accessionNumber === currentAccession)
        ?.shortName ??
      flatTimelineItems.find((i) => i.isCurrentDocument)?.shortName ??
      null
    );
  }, [
    consolidationRenderContext?.changeLaw,
    isConsolidationComparisonEnabled,
    legislation?.externalId,
    flatTimelineItems,
  ]);

  // Diff navigation and filtering — needs docRootRef which is declared above
  const {
    allChangesCount,
    totalChanges,
    insertions,
    deletions,
    currentIndex: diffCurrentIndex,
    goToNext: diffGoToNext,
    goToPrevious: diffGoToPrevious,
    filter: diffFilter,
    setFilter: setDiffFilter,
  } = useDiffNavigation(
    docRootRef,
    diffHtml,
    {
      before:
        activeStepBeforeInfo?.shortName ?? compareTargetInfo?.shortName ?? "",
      after: activeStepAfterInfo?.shortName ?? currentVersionShortName ?? "",
    },
    isComparisonEnabled && diffHtml
      ? {
          before: activeStepBeforeHtml,
          after: activeStepAfterHtml,
        }
      : null
  );

  const handleConsolidationChangeNavigate = useCallback(
    (container: { id: string; type: string }) => {
      if (!instructionPanelOpen) return;
      pendingCloseScrollRestoreRef.current = null;
      instructionScrollAnchorRef.current = null;
      consolidationNavPendingScrollRef.current = container.id;
      setHighlightConsolidationChanges(true);
      setInstructionContainer(container);
    },
    [instructionPanelOpen]
  );

  const consolidationChangeNavEnabled =
    highlightConsolidationChanges && hasChangedContainers && !isCompareMode;

  const {
    currentIndex: consolidationChangeIndex,
    totalChanges: consolidationChangeTotal,
    goToNext: consolidationGoToNext,
    goToPrevious: consolidationGoToPrevious,
  } = useConsolidationChangeNavigation({
    docRootRef,
    changedContainerIds,
    changedContainerGroupKeys,
    enabled: consolidationChangeNavEnabled,
    contentReady: docRootReady,
    activeContainerId: instructionContainer?.id,
    onNavigate: handleConsolidationChangeNavigate,
  });

  /** In compare mode, "before"/"after" views use reconstructed HTML (native layout + highlight spans only). */
  const compareDocumentHtml = useMemo(() => {
    if (!diffHtml) return rawHTML;
    if (diffFilter === "diff") return diffHtml;
    return buildReconstructedDiffViewHtml(
      diffHtml,
      diffFilter === "before" ? "before" : "after"
    );
  }, [diffHtml, diffFilter, rawHTML]);

  const viewerDocumentHtml =
    isCompareMode && diffHtml ? compareDocumentHtml : rawHTML;
  const deferredViewerDocumentHtml = useDeferredValue(viewerDocumentHtml);

  // Document search functionality (enabled for all sources)
  const searchProps = useDocumentSearch({
    seedQuery: documentSearchSeedTerm,
    documentKey: documentId,
  });

  // Convert timeline items to tree structure
  // Bekendtgørelser: already a tree (single root with children)
  // Lov: flat array, convert to single-branch tree
  const timelineRoot = useMemo(() => {
    if (!normalizedTimelineItems || normalizedTimelineItems.length === 0) {
      return null;
    }

    // Already a tree structure (BEK: normalized nested tree, or single node)
    if (normalizedTimelineItems.length === 1) {
      return normalizedTimelineItems[0];
    }

    // Convert flat array to single-branch tree
    // Build tree from end to beginning
    let currentNode: TimelineItem | null = null;
    for (let i = normalizedTimelineItems.length - 1; i >= 0; i--) {
      const item = { ...normalizedTimelineItems[i] };
      if (currentNode) {
        item.children = [currentNode];
      }
      currentNode = item;
    }

    return currentNode;
  }, [normalizedTimelineItems]);

  const isConsolidationTypeBadge =
    !isConsolidationNotFound &&
    (isConsolidatedRender ||
      (Boolean(consolidationRenderContext) && isConsolidationLoading));

  // Activation dates power the "version:" dropdown; only fetched while a
  // consolidation is actually rendered for an anchor law, and scoped to the
  // actively viewed consolidation (change law) so dates from other
  // consolidations in the window are not offered.
  const activationDatesQuery = useConsolidationActivationDates(
    isConsolidationTypeBadge
      ? consolidationRenderContext?.lawExternalId
      : undefined,
    consolidationRenderContext?.changeLaw
  );

  const handleSelectConsolidationVersion = useCallback(
    (date: string) => {
      // Close any open amendment review before the consolidation context
      // changes, mirroring timeline diamond navigation (LAR-306).
      clearReviewStateRef.current();
      onConsolidationAsOf?.(date);
    },
    [onConsolidationAsOf]
  );

  const consolidationVersion = useMemo((): ConsolidationVersionProps | null => {
    if (!isConsolidationTypeBadge || !onConsolidationAsOf || isCompareMode) {
      return null;
    }
    const activationDates = activationDatesQuery.data?.activationDates ?? [];
    if (activationDates.length === 0) return null;
    // The requested `as_of` (URL-derived, updates synchronously on selection)
    // takes priority over dates read from the render result, which lag one
    // fetch behind via React Query placeholder data and would snap the
    // dropdown back to the previous version while the new render loads.
    // Timeline diamond links carry `approvedAt` timestamps in `as_of`; those
    // never match an activation date and fall through to the resolved date.
    return {
      activationDates,
      selectedDateCandidates: [
        consolidationRenderContext?.asOf ?? null,
        consolidationResolvedAsOf,
        consolidationAsOf,
      ],
      onSelectDate: handleSelectConsolidationVersion,
      // Keeps the version badge from calling the newest text of a repealed
      // law "aktuel" right next to the "Ikke-gældende" watermark.
      isHistoric: showHistoricWatermark,
    };
  }, [
    activationDatesQuery.data?.activationDates,
    consolidationAsOf,
    consolidationRenderContext?.asOf,
    consolidationResolvedAsOf,
    handleSelectConsolidationVersion,
    isCompareMode,
    isConsolidationTypeBadge,
    onConsolidationAsOf,
    showHistoricWatermark,
  ]);

  // Floating coverage badge: mirrors the timeline's "consolidated up to" context
  // once the timeline row has scrolled behind the sticky toolbar.
  const { ref: timelineRowRef, isScrolledPast: timelineScrolledPast } =
    useIsScrolledPast({ topOffset: stickyToolbarHeight });

  const showCoverageBadge =
    isConsolidationTypeBadge &&
    !isConsolidationNotFound &&
    !isCompareMode &&
    Boolean(consolidationResolvedAsOf) &&
    timelineScrolledPast;

  // Live review status of the selected change law, resolved from the same
  // per-window metadata that colours the timeline diamonds. This keeps the
  // toolbar badge honest: it shows verified iff the selected diamond is green.
  // Falls back to the silver inbound list only when the live window has no entry.
  const selectedConsolidationStatus = useMemo(():
    InboundChange["status"] | null => {
    const changeLaw = consolidationRenderContext?.changeLaw;
    if (!changeLaw) return null;
    const anchor = consolidationRenderContext?.lawExternalId;
    const windowInbound =
      (anchor ? inboundByAnchor.get(anchor) : undefined) ?? inboundChanges;
    return (
      windowInbound.find((entry) => entry.changelawAccession === changeLaw)
        ?.status ?? null
    );
  }, [consolidationRenderContext, inboundByAnchor, inboundChanges]);

  const currentDocumentTypeDisplay = useMemo(() => {
    if (!documentId) return null;
    // Suppress the document-type badge for standalone documents that have no
    // timeline (e.g. a bekendtgørelse). Without a timeline the metadata
    // fallback mislabels such documents (defaults to "Lov der ændrer"), and the
    // badge only makes sense alongside a timeline or an active consolidation
    // render — both of which imply a timeline exists.
    if (flatTimelineItems.length === 0 && !isConsolidationTypeBadge) {
      return null;
    }
    return resolveCurrentTimelineDocumentDisplay({
      flatTimelineItems,
      activeFlatTimelineIndex,
      timelineSelection,
      isConsolidatedRender: isConsolidationTypeBadge,
      isConsolidationNotFound,
      consolidationStatus: selectedConsolidationStatus,
      fallbackDocumentTypeId: retsinformationMeta?.documentTypeId,
      isTimelineReady,
    });
  }, [
    activeFlatTimelineIndex,
    documentId,
    flatTimelineItems,
    isConsolidationNotFound,
    isConsolidationTypeBadge,
    isTimelineReady,
    retsinformationMeta?.documentTypeId,
    selectedConsolidationStatus,
    timelineSelection,
  ]);

  // Handle paragraph selection - scroll to the element
  const handleParagraphSelect = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    // Add temporary highlight
    element.classList.add("paragraph-highlight");
    setTimeout(() => {
      element.classList.remove("paragraph-highlight");
    }, 2000);

    // Find scroll container - use docRootRef if available, otherwise find from element
    const scroller = docRootRef.current
      ? findScrollContainer(docRootRef.current)
      : findScrollContainer(element);

    if (scroller) {
      // Wait for layout to settle before calculating positions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const containerRect = scroller.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollOffset = calculateScrollOffset(scroller);

          // Calculate element's position relative to scroll container's content
          const elementOffsetFromContainerTop =
            elementRect.top - containerRect.top;
          const elementPositionInContent =
            scroller.scrollTop + elementOffsetFromContainerTop;
          const scrollTop = elementPositionInContent - scrollOffset;

          scroller.scrollTop = Math.max(0, scrollTop);
        });
      });
    } else {
      // Fallback to scrollIntoView
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const isContentAreaLoading =
    isDocumentLoading ||
    (loading && !rawHTML?.trim() && !isConsolidationNotFound);

  const timelineChrome = useMemo(() => {
    if (!timelineRoot || !documentId) return null;

    return (
      <>
        {canSwapTimeline && timelineRootAccession ? (
          <TimelineScopeSwitcher
            timelineScope={timelineScope}
            timelineRootAccession={timelineRootAccession}
            documentId={documentId}
            parentTimelineItems={parentTimelineItems}
            ownTimelineItems={ownTimelineItems}
          />
        ) : null}
        <div
          key={timelineTransitionKey ?? documentId}
          className={cn(
            "transition-opacity duration-200 ease-in-out",
            navigationFading ? "opacity-0" : "opacity-100"
          )}
        >
          <CompactTimeline
            root={timelineRoot}
            currentAccessionNumber={timelineScrollAccession}
            currentFlatTimelineIndex={activeFlatTimelineIndex}
            timelineSelection={timelineSelection}
            datasource={datasource || ""}
            onNavigate={handleTimelineNavigate}
            compareMode={isComparisonEnabled ? isCompareMode : false}
            compareTargetAccessionNumber={compareTargetInfo?.accessionNumber}
            onCompareSelect={
              isComparisonEnabled ? selectCompareTarget : undefined
            }
            eligibleCompareAccessions={eligibleCompareAccessions}
            inboundChanges={inboundChanges}
            inboundByAnchor={inboundByAnchor}
            anchorConsolidationProgress={anchorConsolidationProgress}
            anchorLawAccession={anchorLawAccession}
            showConsolidationDiamonds={showConsolidationDiamondsEffective}
            onOpenConsolidation={handleOpenConsolidationFromTimeline}
          />
        </div>
      </>
    );
  }, [
    activeFlatTimelineIndex,
    anchorConsolidationProgress,
    anchorLawAccession,
    canSwapTimeline,
    compareTargetInfo?.accessionNumber,
    documentId,
    datasource,
    eligibleCompareAccessions,
    handleOpenConsolidationFromTimeline,
    handleTimelineNavigate,
    inboundByAnchor,
    inboundChanges,
    isCompareMode,
    isComparisonEnabled,
    ownTimelineItems,
    parentTimelineItems,
    selectCompareTarget,
    showConsolidationDiamondsEffective,
    navigationFading,
    timelineRoot,
    timelineRootAccession,
    timelineScope,
    timelineScrollAccession,
    timelineSelection,
    timelineTransitionKey,
  ]);

  return (
    <div
      className="flex min-h-full w-full shrink-0 flex-col self-start dark:bg-muted print-bg print-reset"
      style={
        {
          "--legislation-sticky-toolbar-height": `${stickyToolbarHeight}px`,
        } as React.CSSProperties
      }
    >
      {/* Timeline — scrolls away with document; toolbar below stays sticky */}
      {timelineChrome ? (
        <div
          ref={timelineRowRef}
          className="no-print bg-background dark:bg-muted"
        >
          <DocumentViewerToolbar timeline={timelineChrome} />
        </div>
      ) : null}

      {/* Sticky toolbar and comparison bars remain visible while scrolling */}
      <div
        ref={stickyToolbarRef}
        className="sticky top-0 z-30 bg-background dark:bg-muted border-b no-print"
      >
        <DocumentViewerToolbar>
          {legislation && (
            <>
              <LegislationDocumentHeader
                currentDocumentTypeDisplay={currentDocumentTypeDisplay}
                consolidationVersion={consolidationVersion}
                onPrint={() => {
                  void handlePrintDocumentOnly(legislation?.title);
                }}
                originalPdfUrl={originalPdf.url}
                paragraphItems={jumpNavigation.items}
                onParagraphSelect={handleParagraphSelect}
                searchProps={searchProps}
                showParagraphNavigation={showParagraphNavigation}
                paragraphNavigationKind={jumpNavigation.kind}
                documentId={
                  isProcessingIncomplete ? undefined : documentId
                }
                zoomLevel={zoomLevel}
                onZoomChange={setZoomLevel}
                onFitToWidth={handleFitToWidth}
                isCompareMode={isComparisonEnabled ? isCompareMode : undefined}
                onToggleCompareMode={
                  isComparisonEnabled ? toggleCompareMode : undefined
                }
                favoriteSubscribe={favoriteSubscribe}
              />
            </>
          )}
        </DocumentViewerToolbar>

        {showVerifiedBanner && <ConsolidationVerifiedBanner />}

        {isConsolidatedRender &&
          !isConsolidationNotFound &&
          hasChangedContainers &&
          !isCompareMode && (
            <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 lg:px-8 py-2 border-t border-border/60">
              <Highlighter className="size-4 shrink-0 text-muted-foreground" />
              <Label
                htmlFor="highlight-consolidation-changes"
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Fremhæv ændringer
              </Label>
              <Switch
                id="highlight-consolidation-changes"
                checked={highlightConsolidationChanges}
                onCheckedChange={setHighlightConsolidationChanges}
              />
              {highlightConsolidationChanges &&
                consolidationChangeTotal > 0 && (
                  <>
                    <span className="text-muted-foreground hidden sm:inline select-none">
                      |
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        Afsnit med ændring:
                      </span>
                      <button
                        type="button"
                        className="p-0.5 rounded hover:bg-primary/15 transition-colors text-primary disabled:opacity-40"
                        onClick={consolidationGoToPrevious}
                        disabled={consolidationChangeTotal === 0}
                        aria-label="Forrige ændring"
                      >
                        <ChevronUp className="size-3.5" />
                      </button>
                      <span className="text-xs text-primary tabular-nums min-w-12 text-center">
                        {consolidationChangeIndex + 1}/
                        {consolidationChangeTotal}
                      </span>
                      <button
                        type="button"
                        className="p-0.5 rounded hover:bg-primary/15 transition-colors text-primary disabled:opacity-40"
                        onClick={consolidationGoToNext}
                        disabled={consolidationChangeTotal === 0}
                        aria-label="Næste ændring"
                      >
                        <ChevronDown className="size-3.5" />
                      </button>
                    </div>
                  </>
                )}
              <span className="text-xs text-muted-foreground">
                Klik på et ændret afsnit for at sammenligne med
                ændringsinstruktionen
              </span>
            </div>
          )}

        {/* Compare mode hint — shown when compare is active but no target selected yet */}
        {isComparisonEnabled && isCompareMode && !compareTargetInfo && (
          <div className="px-4 sm:px-6 lg:px-8 pb-2">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-warning/50 bg-warning/10 text-warning-foreground dark:bg-warning/20 dark:border-warning/80 dark:text-warning-foreground text-sm">
              <AlertCircle className="size-4 shrink-0" />
              <span>
                {isConsolidationComparisonEnabled ? (
                  <>
                    Vælg en <strong>konsolidering</strong>,{" "}
                    <strong>lovbekendtgørelse</strong> eller{" "}
                    <strong>bekendtgørelse</strong> på tidslinjen for at
                    sammenligne
                  </>
                ) : (
                  <>
                    Vælg en <strong>ældre</strong> version fra tidslinjen for at
                    sammenligne
                  </>
                )}
              </span>
              <button
                className="ml-auto hover:opacity-70 transition-opacity"
                onClick={exitCompareMode}
                aria-label="Afslut sammenligning"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}

        {/* Comparison bar — shown when a version is selected for comparison */}
        {isComparisonEnabled && isCompareMode && compareTargetInfo && (
          <ComparisonBar
            isComputing={isComputing}
            fetchedCount={fetchedCount}
            totalToFetch={totalToFetch}
            steps={steps}
            totalSteps={totalSteps}
            compareStepIndex={compareStepIndex}
            activeStepBeforeInfo={activeStepBeforeInfo}
            activeStepAfterInfo={activeStepAfterInfo}
            compareTargetInfo={compareTargetInfo}
            diffError={diffError}
            diffHtml={diffHtml}
            diffFilter={diffFilter}
            setDiffFilter={setDiffFilter}
            allChangesCount={allChangesCount}
            totalChanges={totalChanges}
            diffCurrentIndex={diffCurrentIndex}
            diffGoToPrevious={diffGoToPrevious}
            diffGoToNext={diffGoToNext}
            deletions={deletions}
            insertions={insertions}
            currentVersionShortName={currentVersionShortName}
            goToPreviousStep={goToPreviousStep}
            goToNextStep={goToNextStep}
            exitCompareMode={exitCompareMode}
          />
        )}
      </div>
      {/* end sticky header */}

      {showOutboundPanel && legislation?.externalId && (
        <OutboundConsolidationsPanel
          outboundChanges={outboundChanges}
          changelawExternalId={legislation.externalId}
          timelineItems={timelineItems}
        />
      )}

      {isProcessingIncomplete && (
        <div className="px-6 no-print">
          <ProcessingBanner />
        </div>
      )}

      {error && !isConsolidationNotFound && (
        <div className="px-6 text-sm" style={{ color: "crimson" }}>
          Fejl: {error}
        </div>
      )}
      {isConsolidationNotFound && (
        <div className="px-4 sm:px-6 lg:px-8 no-print">
          <ConsolidationNotFoundBanner
            hasFallbackHtml={Boolean(rawHTML?.trim())}
          />
        </div>
      )}

      <div
        className={cn(
          isContentAreaLoading
            ? "flex flex-1 flex-col items-center justify-center"
            : "relative min-h-48"
        )}
        ref={containerRef}
      >
        <div
          className={cn(
            "w-full transition-opacity duration-200 ease-in-out",
            navigationFading ? "opacity-0" : "opacity-100",
            isContentAreaLoading &&
              "flex flex-1 flex-col items-center justify-center"
          )}
        >
          {isContentAreaLoading ? (
            <div
              className="flex flex-col items-center gap-2 text-muted-foreground"
              aria-busy
              aria-live="polite"
            >
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              <span className="text-sm">
                {isDocumentLoading
                  ? "Indlæser dokument…"
                  : isConsolidatedRender && !isConsolidationNotFound
                    ? "Henter konsolideret tekst…"
                    : "Henter CSS-filer…"}
              </span>
            </div>
          ) : rawHTML?.trim() ? (
            <LegislationContentErrorBoundary
              resetKey={documentNavigationKey}
              context={{
                documentId,
                changeLaw: consolidationRenderContext?.changeLaw,
                asOf: consolidationRenderContext?.asOf,
                view: consolidationRenderContext?.view,
                timelineAt: timelineAtParam,
              }}
            >
              <div
                className={cn(
                  "flex w-full flex-col gap-0",
                  instructionPanelOpen && "lg:flex-row"
                )}
              >
                <div
                  ref={setViewerColumnEl}
                  className="relative min-w-0 flex-1 px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8"
                >
                  <LegislationPrintHeader legislation={legislation} />
                  <ConsolidationCoverageBadge
                    resolvedAsOf={consolidationResolvedAsOf}
                    visible={showCoverageBadge}
                  />
                  <DocumentRenderer
                    key={documentId}
                    rawHTML={deferredViewerDocumentHtml}
                    rawCSS={rawCSS}
                    highlightIds={
                      isProcessingIncomplete || isCompareMode
                        ? []
                        : highlightIds
                    }
                    scrollToId={
                      isProcessingIncomplete || isCompareMode
                        ? undefined
                        : scrollToId
                    }
                    highlightMatchText={
                      isProcessingIncomplete ? undefined : highlightMatchText
                    }
                    highlightChunkText={
                      isProcessingIncomplete ? undefined : highlightChunkText
                    }
                    currentSource={currentSource}
                    currentDocId={
                      isProcessingIncomplete ? undefined : documentId
                    }
                    // Compare mode renders a diff of the same id, so it gets its
                    // own remembered position rather than sharing the plain one.
                    scrollMemoryKey={
                      documentId
                        ? `legislation:${currentSource}:${documentId}${isCompareMode ? ":compare" : ""}`
                        : undefined
                    }
                    isCurrent={documentIsCurrent}
                    isProcessingIncomplete={isProcessingIncomplete}
                    isConsolidatedRender={
                      isConsolidatedRender && !isConsolidationNotFound
                    }
                    consolidationContext={consolidationLinkContext}
                    changedContainerIds={changedContainerIds}
                    changedContainerGroupKeys={changedContainerGroupKeys}
                    changedContainerStatusById={changedContainerStatusById}
                    deletedContainerChanges={deletedContainerChanges}
                    highlightConsolidationChanges={
                      highlightConsolidationChanges && hasChangedContainers
                    }
                    onChangedContainerClick={
                      hasChangedContainers && !isCompareMode
                        ? handleChangedContainerClick
                        : undefined
                    }
                    containerAmendmentReview={
                      isCompareMode ? null : containerAmendmentReview
                    }
                    zoomLevel={zoomLevel}
                    onContentCommitted={searchProps.onContentCommitted}
                  />
                </div>

                {instructionPanelOpen && (
                  <aside
                    className={cn(
                      "no-print z-10 shrink-0 border-border bg-background",
                      // mobile: sticky bottom drawer
                      "sticky bottom-0 max-h-[55vh] overflow-hidden border-t",
                      // desktop: sticky right column, offset below document toolbar
                      "lg:bottom-auto lg:top-[var(--legislation-sticky-toolbar-height,0px)] lg:max-h-none lg:h-[calc(100vh-var(--appbar-height)-var(--legislation-sticky-toolbar-height,0px))] lg:w-[380px] lg:self-start lg:border-l lg:border-t-0 xl:w-[440px]"
                    )}
                  >
                    <ConsolidationChangeInstructionPanel
                      containerType={instructionContainer?.type}
                      data={instructionChangeData}
                      isLoading={isInstructionLoading || isComputingDiff}
                      isError={isInstructionError}
                      cannotHighlightDiff={
                        cannotHighlightDiff || diffComputeError
                      }
                      isFullInsertion={isFullInsertion}
                      isFullDeletion={isFullDeletion}
                      onClose={handleCloseInstructionPanel}
                      timelineItems={timelineItems}
                      onNavigateToChangeLaw={
                        onConsolidationChangeLaw
                          ? handleNavigateToChangeLaw
                          : undefined
                      }
                      feedback={instructionPanelFeedback}
                    />
                  </aside>
                )}
              </div>
            </LegislationContentErrorBoundary>
          ) : isConsolidationNotFound && !loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center text-muted-foreground sm:px-6 lg:px-8">
              <FileSearch className="size-10 opacity-40" />
              <p className="text-sm max-w-md">
                Der findes endnu ingen konsolideret tekst for dette dokument.
              </p>
            </div>
          ) : null}
        </div>
      </div>
      <div className="no-print">
        <ScrollToTopFab
          boundsElement={instructionPanelOpen ? viewerColumnEl : undefined}
        />
      </div>
      {isComparisonEnabled &&
        isCompareMode &&
        compareTargetInfo &&
        diffHtml &&
        !isComputing &&
        !diffError &&
        totalChanges > 0 && (
          <DiffNavigationFab
            currentIndex={diffCurrentIndex}
            totalChanges={totalChanges}
            onPrevious={diffGoToPrevious}
            onNext={diffGoToNext}
          />
        )}
      {consolidationChangeNavEnabled && consolidationChangeTotal > 0 && (
        <DiffNavigationFab
          variant="primary"
          currentIndex={consolidationChangeIndex}
          totalChanges={consolidationChangeTotal}
          onPrevious={consolidationGoToPrevious}
          onNext={consolidationGoToNext}
          boundsElement={instructionPanelOpen ? viewerColumnEl : undefined}
          previousLabel="Forrige ændring"
          nextLabel="Næste ændring"
          groupLabel="Naviger mellem ændrede afsnit"
        />
      )}
    </div>
  );
};

export default LegislationDocumentContent;
